/**
 * Centralized error handling for the application
 * Provides consistent user feedback and logging
 */

import { toast } from './toast';

// Extended Error type for API errors
export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

// Error type enum
export enum ErrorType {
  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  FETCH_ERROR = 'FETCH_ERROR',
  
  // Validation
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  DUPLICATE_NAME = 'DUPLICATE_NAME',
  INVALID_FORMAT = 'INVALID_FORMAT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  
  // Auth
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  
  // Not found
  NOT_FOUND = 'NOT_FOUND',
  PROJECT_NOT_FOUND = 'PROJECT_NOT_FOUND',
  DOCUMENT_NOT_FOUND = 'DOCUMENT_NOT_FOUND',
  
  // Server
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR = 'DATABASE_ERROR',
  
  // Business logic
  CONFLICT = 'CONFLICT',
  RATE_LIMIT = 'RATE_LIMIT',
}

/**
 * Map HTTP status code to ErrorType
 */
function getErrorType(status: number): ErrorType {
  if (status === 400) return ErrorType.VALIDATION_ERROR;
  if (status === 401) return ErrorType.UNAUTHORIZED;
  if (status === 403) return ErrorType.FORBIDDEN;
  if (status === 404) return ErrorType.NOT_FOUND;
  if (status === 409) return ErrorType.CONFLICT;
  if (status === 429) return ErrorType.RATE_LIMIT;
  if (status === 503) return ErrorType.SERVICE_UNAVAILABLE;
  if (status >= 500) return ErrorType.SERVER_ERROR;
  
  return ErrorType.SERVER_ERROR; // Default
}

// User-friendly error messages map
const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  FETCH_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT: 'Request timed out. Please try again.',
  NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  
  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again.',
  DUPLICATE_NAME: 'A project with this name already exists. Please choose a different name.',
  INVALID_FORMAT: 'Invalid data format. Please check your input.',
  MISSING_REQUIRED_FIELD: 'Please fill in all required fields.',
  
  // Auth errors
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You don\'t have permission to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  
  // Not found
  NOT_FOUND: 'The requested resource was not found.',
  PROJECT_NOT_FOUND: 'Project not found. It may have been deleted.',
  DOCUMENT_NOT_FOUND: 'Document not found.',
  
  // Server errors
  SERVER_ERROR: 'Something went wrong on our end. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',
  DATABASE_ERROR: 'Database error occurred. Please try again.',
  
  // Business logic
  CONFLICT: 'This action conflicts with existing data.',
  RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
};

export interface ErrorHandlerOptions {
  context?: string;              // What was being done? e.g., "Creating project"
  fallback?: string;             // Custom error message
  showToast?: boolean;           // Show toast notification (default: true)
  retry?: () => void | Promise<void>;  // Retry function for toast action button (can be async)
  onError?: (error: ApiError) => void;  // Custom error callback
  logToConsole?: boolean;        // Log to console (default: true in dev)
}

// Helper: Map status code to error code and message
function getStatusBasedError(status: number): { code: string; message: string } {
  const STATUS_MAP: Record<number, { code: string; message: string }> = {
    400: { code: 'VALIDATION_ERROR', message: ERROR_MESSAGES.VALIDATION_ERROR },
    401: { code: 'UNAUTHORIZED', message: ERROR_MESSAGES.UNAUTHORIZED },
    403: { code: 'FORBIDDEN', message: ERROR_MESSAGES.FORBIDDEN },
    404: { code: 'NOT_FOUND', message: ERROR_MESSAGES.NOT_FOUND },
    409: { code: 'DUPLICATE_NAME', message: ERROR_MESSAGES.DUPLICATE_NAME },
    429: { code: 'RATE_LIMIT', message: ERROR_MESSAGES.RATE_LIMIT },
    503: { code: 'SERVICE_UNAVAILABLE', message: ERROR_MESSAGES.SERVICE_UNAVAILABLE },
  };

  if (STATUS_MAP[status]) return STATUS_MAP[status];
  if (status >= 500) return { code: 'SERVER_ERROR', message: ERROR_MESSAGES.SERVER_ERROR };
  
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred' };
}

// Helper: Parse error message from ApiError
function parseErrorMessage(apiError: ApiError): { code: string; message: string } {
  // Priority 1: Check HTTP status
  if (apiError.status) {
    return getStatusBasedError(apiError.status);
  }

  // Priority 2: Check message content
  const lowerMessage = apiError.message.toLowerCase();
  if (lowerMessage.includes('fetch')) {
    return { code: 'FETCH_ERROR', message: ERROR_MESSAGES.FETCH_ERROR };
  }
  if (lowerMessage.includes('timeout')) {
    return { code: 'TIMEOUT', message: ERROR_MESSAGES.TIMEOUT };
  }

  // Priority 3: Use backend error code
  if (apiError.code && ERROR_MESSAGES[apiError.code]) {
    return { code: apiError.code, message: ERROR_MESSAGES[apiError.code] };
  }

  // Priority 4: Use raw message if user-friendly
  if (apiError.message) {
    return { code: 'CUSTOM_ERROR', message: apiError.message };
  }

  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred' };
}

// Helper: Log error to console (dev only)
function logErrorToConsole(apiError: ApiError, context?: string, errorCode?: string, errorStatus?: number) {
  if (process.env.NODE_ENV !== 'development') return;

  console.error('API Error:', {
    context,
    message: apiError.message,
    status: errorStatus,
    code: errorCode,
    details: apiError.details,
    stack: apiError.stack,
    timestamp: new Date().toISOString()
  });
}

// Helper: Show toast with retry option
function showErrorToast(message: string, retry?: () => void | Promise<void>) {
  toast.error(message, {
    duration: 5000,
    action: retry ? {
      label: 'Retry',
      onClick: () => { retry(); }
    } : undefined
  });
}

/**
 * Centralized API error handler
 * Provides consistent user feedback, logging, and error recovery
 * 
 * @example
 * try {
 *   await createProject(data);
 * } catch (error) {
 *   handleApiError(error, {
 *     context: 'Creating project',
 *     retry: () => createProject(data)
 *   });
 * }
 */
export function handleApiError(
  error: unknown,
  options?: ErrorHandlerOptions
): void {
  const {
    context,
    fallback,
    showToast = true,
    retry,
    onError,
    logToConsole = true
  } = options || {};

  // Handle non-Error objects
  if (!(error instanceof Error)) {
    const errorMessage = String(error);
    if (logToConsole && process.env.NODE_ENV === 'development') {
      console.error('Unknown Error:', { context, error: errorMessage, timestamp: new Date().toISOString() });
    }
    if (showToast) {
      const message = context ? `${context}: ${errorMessage}` : errorMessage;
      toast.error(message, { duration: 5000 });
    }
    return;
  }

  const apiError = error as ApiError;
  
  // Parse error to get user-friendly message
  const { code: errorCode, message: errorMessage } = parseErrorMessage(apiError);
  const userMessage = fallback || (context ? `${context}: ${errorMessage}` : errorMessage);

  // Log to console
  if (logToConsole) {
    logErrorToConsole(apiError, context, errorCode, apiError.status);
  }

  // Show toast
  if (showToast) {
    showErrorToast(userMessage, retry);
  }

  // Custom error callback
  if (onError) {
    onError(apiError);
  }
}

/**
 * Create an API error from a fetch Response
 * Extracts error details from response body
 */
export async function createApiError(
  response: Response,
  context?: string
): Promise<ApiError> {
  let errorMessage = `Request failed with status ${response.status}`;
  let errorCode: string | undefined;
  let errorDetails: any;
  
  try {
    const contentType = response.headers.get('content-type');
    
    if (contentType?.includes('application/json')) {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorData.detail || errorMessage;
      errorCode = errorData.code;
      errorDetails = errorData.details || errorData;
    } else {
      const textError = await response.text();
      if (textError) {
        errorMessage = textError.substring(0, 200); // Limit length
      }
    }
  } catch {
    // If parsing fails, use default message
  }
  
  const error = new Error(errorMessage) as ApiError;
  error.status = response.status;
  error.code = errorCode;
  error.details = errorDetails;
  
  return error;
}

/**
 * Wrapper for fetch with automatic error handling
 * 
 * @example
 * const data = await fetchWithErrorHandling('/api/projects', {
 *   method: 'POST',
 *   body: JSON.stringify(projectData)
 * }, { context: 'Creating project' });
 */
export async function fetchWithErrorHandling<T = any>(
  url: string,
  options?: RequestInit,
  errorOptions?: ErrorHandlerOptions
): Promise<T> {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const apiError = await createApiError(response, errorOptions?.context);
      throw apiError;
    }
    
    return await response.json();
  } catch (error) {
    handleApiError(error, errorOptions);
    throw error; // Re-throw for caller to handle if needed
  }
}

/**
 * Check if an error is a specific type
 */
export function isErrorType(error: unknown, type: ErrorType): boolean {
  if (!(error instanceof Error)) return false;
  
  const apiError = error as ApiError;
  return apiError.code === type;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  return (
    error.message.includes('fetch') ||
    error.message.includes('network') ||
    error.message.includes('Failed to fetch')
  );
}

/**
 * Get user-friendly error message from error object
 */
export function getUserFriendlyMessage(error: unknown, fallback = 'An error occurred'): string {
  if (!(error instanceof Error)) return fallback;
  
  const apiError = error as ApiError;
  
  // Check if we have a predefined message for this code
  if (apiError.code && ERROR_MESSAGES[apiError.code]) {
    return ERROR_MESSAGES[apiError.code];
  }
  
  // Check status code
  if (apiError.status) {
    const errorType = getErrorType(apiError.status);
    if (ERROR_MESSAGES[errorType]) {
      return ERROR_MESSAGES[errorType];
    }
  }
  
  // Use error message if it looks user-friendly
  if (apiError.message && !apiError.message.includes('fetch') && apiError.message.length < 100) {
    return apiError.message;
  }
  
  return fallback;
}

